import React, { useState, useEffect } from 'react';
import {
  Plus,
  Edit3,
  Loader2,
  AlertCircle,
  RefreshCw,
} from 'lucide-react';

import { IserviceResponse } from '../../../types/Response/AdminResponse';
import {
  useLazyGetAllServiceQuery,
  useUpdateServiceMutation,
} from '../../../redux/actions/Admin/ServiceAdmin';

import ServiceFormScreen from './ServiceFormScreen';

// Helper map to display friendly labels for Service Types
const SERVICE_TYPE_LABELS: Record<string, string> = {
  Grooming: 'Grooming',
  Training: 'Training',
  VetCare: 'Vet Care',
  PetTaxi: 'Pet Taxi',
  Relocation: 'Relocation',
  Walking: 'Walking',
  DayCare: 'Day Care',
  Other: 'Other',
};

export default function ManageServices() {
  const [triggerGetAllServices, {
    data: services = [],
    isLoading,
    isError,
  }] = useLazyGetAllServiceQuery();

  const [updateService] = useUpdateServiceMutation();

  const [currentView, setCurrentView] = useState<'list' | 'add' | 'edit'>('list');
  const [selectedService, setSelectedService] = useState<IserviceResponse | null>(null);

  useEffect(() => {
    triggerGetAllServices();
  }, [triggerGetAllServices]);

  // Handle active status toggle without dropping serviceType
  const handleToggleActive = async (
    service: IserviceResponse,
    e: React.MouseEvent
  ) => {
    e.stopPropagation();

    try {
      await updateService({
        id: service.id,
        name: service.name,
        description: service.description,
        logoUrl: service.logoUrl,
        serviceType: service.serviceType,
        isActive: !service.isActive,
      }).unwrap();
    } catch (error) {
      console.error('Failed to update service status state:', error);
    }
  };

  const sortedServices = [...services].sort((a, b) =>
    a.name.localeCompare(b.name)
  );

  if (currentView !== 'list') {
    return (
      <ServiceFormScreen
        mode={currentView as 'add' | 'edit'}
        initialData={selectedService}
        onClose={() => {
          setCurrentView('list');
          setSelectedService(null);
        }}
      />
    );
  }

  return (
    <div className="min-h-screen bg-white p-8 antialiased">
      <div className="max-w-[1400px] mx-auto">

        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-5 mb-6 border-b border-gray-100 pb-6">
          <div>
            <h1 className="text-2xl font-bold text-[#234F1E]">
              Services Management
            </h1>
          </div>

          <button
            onClick={() => {
              setSelectedService(null);
              setCurrentView('add');
            }}
            className="bg-[#234F1E] hover:bg-[#183915] text-white px-6 py-3 rounded-xl flex items-center gap-2 text-sm font-semibold transition-all duration-300 shadow-sm hover:shadow cursor-pointer"
          >
            <Plus className="w-4 h-4 stroke-[2.5]" />
            Add Service
          </button>
        </div>

        {/* Dynamic State Rendering */}
        {isLoading ? (
          <div className="flex flex-col items-center justify-center h-[350px]">
            <Loader2 className="w-10 h-10 animate-spin text-[#6DBE45]" />
            <p className="mt-3 text-sm font-semibold text-gray-400 uppercase tracking-wider">
              Loading service matrices...
            </p>
          </div>
        ) : isError ? (
          <div className="bg-white rounded-2xl p-10 text-center border border-red-100 max-w-md mx-auto">
            <AlertCircle className="w-10 h-10 text-red-500 mx-auto" />
            <h2 className="mt-4 text-lg font-bold text-gray-800">
              Failed to load services
            </h2>
            <p className="text-gray-400 text-sm mt-1">
              Something went wrong while compiling database streams.
            </p>
            <button
              onClick={() => triggerGetAllServices()}
              className="mt-5 bg-[#234F1E] text-white px-5 py-2.5 rounded-xl flex items-center gap-2 mx-auto text-sm font-semibold hover:bg-[#183915] transition cursor-pointer"
            >
              <RefreshCw className="w-4 h-4" />
              Retry
            </button>
          </div>
        ) : (
          <div>
            {sortedServices.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-x-8 gap-y-12 justify-center">
                {sortedServices.map((service) => (
                  <div
                    key={service.id}
                    onClick={() => {
                      setSelectedService(service);
                      setCurrentView('edit');
                    }}
                    className="group flex flex-col items-center cursor-pointer transition-all duration-300 relative"
                  >

                    {/* Organic Pebble Shape Image Container */}
                    <div className="relative size-[180px] mx-auto">
                      <div
                        className={`w-full h-full overflow-hidden border-[3px] transition-all duration-500 bg-gray-50 
                          ${service.isActive
                            ? 'border-[#6DBE45] rounded-[48%_52%_68%_32%_/_42%_48%_52%_58%] group-hover:rounded-[52%_48%_35%_65%_/_48%_55%_45%_52%] group-hover:shadow-xl'
                            : 'border-gray-300 rounded-[50%] opacity-65 shadow-xs'
                          }`}
                      >
                        <img
                          src={
                            service.logoUrl ||
                            'https://images.unsplash.com/photo-1517849845537-4d257902454a?w=400'
                          }
                          alt={service.name}
                          className={`w-full h-full object-cover transition-all duration-500 group-hover:scale-105 ${
                            !service.isActive ? 'grayscale filter contrast-75 brightness-95' : ''
                          }`}
                        />
                      </div>
                    </div>

                    {/* Service Info & Type Badge */}
                    <div className="mt-4 text-center space-y-1 w-full px-1 flex flex-col items-center">
                      <h3 className="text-lg font-bold text-[#234F1E] group-hover:text-[#6DBE45] transition-colors duration-200 line-clamp-1">
                        {service.name}
                      </h3>

                      {/* Service Type Tag */}
                      {service.serviceType && (
                        <span className="inline-block px-2.5 py-0.5 text-[11px] font-semibold text-[#234F1E] bg-emerald-50 border border-emerald-100 rounded-full">
                          {SERVICE_TYPE_LABELS[service.serviceType] || service.serviceType}
                        </span>
                      )}

                      {service.description && (
                        <p className="text-gray-400 text-xs line-clamp-2 max-w-[180px] mx-auto leading-normal">
                          {service.description}
                        </p>
                      )}
                    </div>

                    {/* Active/Inactive Toggle Pill */}
                    <div className="mt-3.5 flex items-center justify-center gap-2">
                      <button
                        type="button"
                        onClick={(e) => handleToggleActive(service, e)}
                        className={`relative w-11 h-6 rounded-full transition-all duration-300 ease-in-out shadow-xs cursor-pointer ${
                          service.isActive ? 'bg-[#6DBE45]' : 'bg-gray-200'
                        }`}
                      >
                        <div
                          className={`absolute top-1 w-4 h-4 bg-white rounded-full shadow-md transition-all duration-300 ease-in-out ${
                            service.isActive ? 'translate-x-6' : 'translate-x-1'
                          }`}
                        />
                      </button>
                    </div>

                    {/* Edit Hover Indicator */}
                    <div className="mt-2 text-[9px] font-bold text-gray-400 uppercase tracking-widest opacity-0 group-hover:opacity-100 transition-opacity duration-300 inline-flex items-center gap-1">
                      <Edit3 className="w-3 h-3 text-[#6DBE45]" /> Edit Fields
                    </div>

                  </div>
                ))}
              </div>
            ) : (
              <div className="h-[250px] flex items-center justify-center text-gray-400 font-medium text-sm border-2 border-dashed border-gray-200 rounded-2xl">
                No active service modules found. Click "Add Service" to initiate custom loops.
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}